import { PlusOutlined } from '@ant-design/icons'
import { ModalForm, ModalFormProps } from '@ant-design/pro-components'
import { FormattedMessage, useIntl } from '@umijs/max'
import { Button, ButtonProps, message } from 'antd'
import { useState } from 'react'

type TableModalFormProps = {
  title?: string
  label?: string
  icon?: React.ReactNode
  resource: string
  col: number
  buttonType: 'create' | 'edit' | string
  onSubmit: (data: any) => Promise<void>
} & ModalFormProps

export const CreateButton: React.FC<{ label?: string; icon?: React.ReactNode } & ButtonProps> = ({
  label,
  icon,
  ...props
}) => {
  return (
    <Button type='primary' {...props}>
      {icon || <PlusOutlined />} <FormattedMessage id={label || 'pages.option.create'} />
    </Button>
  )
}

export const EditButton: React.FC<{ label?: string } & ButtonProps> = ({ label, ...props }) => {
  return (
    <a {...props}>
      <FormattedMessage id={label || 'pages.option.edit'} />
    </a>
  )
}

export const DefaultButton: React.FC<{ label?: string } & ButtonProps> = ({ label, ...props }) => {
  return (
    <a {...props}>
      <FormattedMessage id={label || '默认'} />
    </a>
  )
}

/**
 * 创建或编辑的表单弹窗
 * @param resource 资源名称，用于获取国际化文本，例：pages.${resource}.option.create
 * @param col 列数，决定弹窗宽度
 * @param buttonType 按钮类型，决定是创建还是编辑
 * @param children 表单内容
 * @param reload 点击确认后的刷新函数，例：actionRef.current?.reload()
 * @param onSubmit 点击确认后的回调函数
 */
export const TableModalForm: React.FC<TableModalFormProps> = ({
  title,
  label,
  icon,
  buttonType,
  resource,
  col = 2,
  children,
  onSubmit,
  ...props
}) => {
  const intl = useIntl()
  const [open, setOpen] = useState(false)

  return (
    <ModalForm
      open={open}
      onOpenChange={setOpen}
      title={intl.formatMessage({
        id: title || `pages.${resource}.option.${buttonType}`,
      })}
      width={`${368 * col}px`}
      onFinish={async (data) => {
        const msgText = intl.formatMessage({
          id: title || `pages.${resource}.option.${buttonType}`,
        })

        try {
          await onSubmit(data)
          message.success(`${msgText}成功`)
          return true
        } catch (err: any) {
          if (err?.notified) return false

          switch (err.cause) {
            case 'warning':
              message.warning(err.message)
              break
            default:
              message.error(err.message)
          }
          return false
        }
      }} 
      trigger={
        buttonType === 'create' || buttonType === 'batchCreate' ? (
          <CreateButton
            label={label}
            icon={icon}
            onClick={() => {
              setOpen(true)
            }}
          />
        ) : buttonType === 'edit' ? (
          <EditButton
            label={label}
            onClick={() => {
              setOpen(true)
            }}
          />
        ) : (
          <DefaultButton
            label={label || `pages.${resource}.option.${buttonType}`}
            onClick={() => {
              setOpen(true)
            }}
          />
        )
      }
      modalProps={{
        destroyOnHidden: true,
      }}
      {...props}
    >
      {children}
    </ModalForm>
  )
}
